import { useState, useCallback, MouseEvent, useEffect, useMemo, useLayoutEffect } from 'react';
import { useSelector } from "react-redux";
import { getIsLocationPlatformMobileSelector } from "../modules/Location/locationSelectors";
import { ResizeObserver } from '@juggle/resize-observer';

type TScrollBehavior = 'natural' | 'reverse';

interface IParams {
    behavior?: TScrollBehavior;
    desktopOnly?: boolean;
    withGrabCursor?: boolean;
}

function useHorizontalScroll<T extends HTMLElement = HTMLElement>(params?: IParams) {
    const { behavior = 'reverse', desktopOnly = true, withGrabCursor = true } = params ?? {};
    const [scrollNode, setScrollNode] = useState<T>(null);
    const [clickStartX, setClickStartX] = useState<number | null>(null);
    const [scrollStartX, setScrollStartX] = useState<number | null>(null);
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [isMouseMoved, setIsMouseMoved] = useState(false);
    const isPlatformMobile = useSelector(getIsLocationPlatformMobileSelector);
    const isUnavailable = desktopOnly && isPlatformMobile;
    const isDragging = isMouseDown && isMouseMoved;
    const [isOverflowing, setIsOverflowing] = useState(false);

    const handleDragStart = useCallback((e: MouseEvent<T>) => {
        if (scrollNode) {
            setClickStartX(e.screenX);
            setScrollStartX(scrollNode.scrollLeft);
            setIsMouseDown(true);
        }
    }, [scrollNode]);

    const handleDragMove = useCallback(
        (e: MouseEvent<T>) => {
            if (scrollNode) {
                e.preventDefault();
                e.stopPropagation();

                if (clickStartX !== null && scrollStartX !== null && isMouseDown) {
                    const touchDelta = clickStartX - e.screenX;
                    if (Math.abs(touchDelta) < 5) return;
                    setIsMouseMoved(true);
                    scrollNode.scrollLeft = behavior === 'natural' ? scrollStartX - touchDelta : scrollStartX + touchDelta;
                }
            }
        },
        [clickStartX, isMouseDown, scrollStartX, behavior, scrollNode],
    );

    const handleDragEnd = useCallback(() => {
        setClickStartX(null);
        setScrollStartX(null);
        setIsMouseDown(false);
        setIsMouseMoved(false);
    }, []);

    const handleScrollLeft = useCallback((e: MouseEvent) => {
        e.stopPropagation();
        scrollNode.scrollBy({ left: -scrollNode.clientWidth, behavior: 'smooth' });
    }, [scrollNode]);

    const handleScrollRight = useCallback((e: MouseEvent) => {
        e.stopPropagation();
        scrollNode.scrollBy({ left: scrollNode.clientWidth, behavior: 'smooth' });
    }, [scrollNode]);

    const props = useMemo(() => (isUnavailable ? {} : {
        onMouseDown: handleDragStart,
        onMouseMove: handleDragMove,
        onMouseUp: handleDragEnd,
        onMouseLeave: handleDragEnd,
    }), [isUnavailable, handleDragStart, handleDragMove, handleDragEnd]);

    useEffect(() => {
        const isOverflow = scrollNode?.scrollWidth > scrollNode?.clientWidth;
        if (!withGrabCursor || !scrollNode || !isOverflow) return;
        scrollNode.style.cursor = isDragging ? 'grabbing' : 'pointer';
    }, [withGrabCursor, isDragging, scrollNode]);

    useLayoutEffect(() => {
        if (!scrollNode) return;

        const resizeObserver = new ResizeObserver((entries) => {
            setIsOverflowing(entries[0].target.scrollWidth > entries[0].target.clientWidth);
        });

        if (scrollNode) {
            resizeObserver.observe(scrollNode);
        }

        return () => {
            if (scrollNode) {
                resizeObserver.unobserve(scrollNode);
                resizeObserver.disconnect();
            }
        };
    }, [scrollNode]);

    return {
        props,
        scrollRef: setScrollNode,
        isDragging: isUnavailable ? false : isDragging,
        onScrollLeft: handleScrollLeft,
        onScrollRight: handleScrollRight,
        isOverflowing,
    };
}

export default useHorizontalScroll;